import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { MotionConfig } from "framer-motion";
import SmartImg from "../components/SmartImg";
import { Link } from "react-router-dom";
import { ArrowRight, Lightning, Crown, MusicNotes, FilmStrip, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useCopy } from "../context/CopyContext";
import TrendingRail from "../components/home/TrendingRail";
import JustDropped from "../components/JustDropped";
import { Marquee } from "../components/home/motion";
import { AudiosSection, EditsSection, AaPlusTeaser, SectionHead } from "../components/home/Sections";
import PackDrawer from "../components/PackDrawer";
import DailyInspoPopup from "../components/DailyInspoPopup";
import { EDITS_LIVE } from "../lib/features";

function useTypewriter(text, { speed = 38, start = true, startDelay = 0 } = {}) {
  const [output, setOutput] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!start || !text) return undefined;

    const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setOutput(text);
      setDone(true);
      return undefined;
    }

    setOutput("");
    setDone(false);
    let i = 0;
    let interval;

    const startTimer = setTimeout(() => {
      interval = setInterval(() => {
        i += 1;
        setOutput(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
    }, startDelay);

    return () => {
      clearTimeout(startTimer);
      clearInterval(interval);
    };
  }, [text, start, speed, startDelay]);

  return { output, done };
}

function TypewriterHeading({ line1, line2 }) {
  // Split line1 at the first word so "Stop" / "searching." (or similar) type on
  // their own pre-committed lines instead of letting the browser reflow the
  // partial word onto a new line mid-animation (the "jump" glitch).
  const words1 = line1.split(" ");
  const line1Word1 = words1[0] || "";
  const line1Rest = words1.slice(1).join(" ");

  const { output: out1a, done: done1a } = useTypewriter(line1Word1, { speed: 38 });
  const { output: out1b, done: done1b } = useTypewriter(line1Rest, { speed: 38, start: done1a, startDelay: 60 });
  const { output: out2, done: done2 } = useTypewriter(line2, { speed: 38, start: done1b, startDelay: 200 });

  const headingClasses = "font-display font-black text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tighter mb-6";

  return (
    <div className="relative">
      {/* Invisible sizer holding the FULL final text, pre-broken on the same fixed
          lines as the animated version below — reserves the exact final height and
          line positions from the first frame so nothing ever shifts while typing. */}
      <h1 aria-hidden="true" className={`${headingClasses} invisible select-none`}>
        {line1Word1}
        <br />
        {line1Rest}
        <br />
        {line2}
      </h1>

      {/* Animated text, laid exactly over the sizer above */}
      <h1 className={`${headingClasses} absolute inset-0`}>
        <span className="sr-only">{line1} {line2}</span>
        <span aria-hidden="true">
          <span className="gradient-emphasis">{out1a}</span>
          {!done1a && <span className="typewriter-cursor" />}
          <br />
          <span className="gradient-emphasis">{out1b}</span>
          {done1a && !done1b && <span className="typewriter-cursor" />}
          <br />
          <span className="gradient-emphasis">{out2}</span>
          {done1b && !done2 && <span className="typewriter-cursor" />}
        </span>
      </h1>
    </div>
  );
}

export default function Home() {
  const { t } = useCopy();
  const [trendingPacks, setTrendingPacks] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [trendingAudios, setTrendingAudios] = useState([]);
  const [edits, setEdits] = useState([]);
  const [openPackId, setOpenPackId] = useState(null);
  const [inspo, setInspo] = useState(null);

  useEffect(() => {
    // one card per character (every version counted together), most downloaded first
    api.get("/scenepacks/characters?sort=trending&limit=24")
      .then((r) => setTrendingPacks(r.data.items.map((it) => ({
        id: it.top_pack_id,
        key: it.key,
        character_name: it.character_name,
        title: it.character_name,
        movie_title: it.celebrity ? "Celebrity" : it.movie_title,
        year: it.celebrity ? 0 : it.year,
        thumbnail_url: it.thumbnail_url,
        qualities: it.qualities,
        quality: it.qualities[0] || "",
        premium: it.premium,
        downloads: it.downloads,
        likes: it.likes,
        versions: it.versions,
      }))))
      .finally(() => setTrendingLoading(false));
    api.get("/audios?sort=trending&limit=6").then((r) => setTrendingAudios(r.data));
    if (EDITS_LIVE) api.get("/edits?limit=4").then((r) => setEdits(r.data));
    api.get("/daily-inspo").then((r) => setInspo(r.data.inspo));
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div data-testid="home-page">
      <DailyInspoPopup inspo={inspo} onOpenPack={setOpenPackId} />

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden">
        <PosterWall packs={trendingPacks} loading={trendingLoading} />

        <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16 md:pb-20 relative z-10">
          <div className="max-w-3xl fade-up">
            <div className="chip chip-garnet mb-6">
              <Sparkle size={12} weight="fill" /> {t("home.hero.eyebrow", "The editor's resource platform")}
            </div>
            <TypewriterHeading
              line1={t("home.hero.title_line1", "Cut deeper.")}
              line2={t("home.hero.title_line2", "Score louder.")}
            />
            <p className="text-white/60 text-lg max-w-2xl leading-relaxed mb-10">
              {t("home.hero.subtitle", "Cinema-grade scenepacks in 4K, REMUX & 1080p. Hand-picked audio edits across aesthetic, soft, film & velo. Built for editors who refuse to be average.")}
            </p>
            <div className="flex flex-wrap gap-4 mb-14">
              <Link to="/scenepacks" className="btn-primary" data-testid="hero-cta-scenepacks">
                {t("home.hero.cta_primary", "Browse Scenepacks")} <ArrowRight size={16} />
              </Link>
              <Link to="/audios" className="btn-secondary" data-testid="hero-cta-audios">
                {t("home.hero.cta_secondary", "Explore Audios")}
              </Link>
            </div>

            {/* Stat line — a single editorial strip instead of boxed number tiles */}
            <div className="flex items-center gap-0 max-w-xl border-t border-white/10 pt-6">
              <StatInline value={t("home.stat_scenepacks", "120+")} label="Scenepacks" />
              <WaveTick />
              <StatInline value={t("home.stat_audios", "5000+")} label="Audio Edits" />
              <WaveTick />
              <StatInline value={t("home.stat_creators", "30+")} label="Creators" />
            </div>
          </div>
        </div>

        {/* Signature waveform seam — closes out the hero, opens the rest of the page */}
        <WaveformSeam />
      </section>

      {/* ================= FEATURE BAND ================= */}
      <section className="bg-[#101010]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          <FeatureItem icon={<FilmStrip size={20} />} title="4K · REMUX · 1080p" desc="Quality you can actually edit with" />
          <FeatureItem icon={<MusicNotes size={20} />} title="Waveform Previews" desc="Audition before you download" />
          <FeatureItem icon={<Lightning size={20} />} title="Updated Daily" desc="Fresh scenepacks every drop" />
          <FeatureItem icon={<Crown size={20} />} title="AA+ Premium" desc="Exclusive cuts for members" />
        </div>
      </section>

      {/* ================= JUST DROPPED — what was added lately ================= */}
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 pt-16 md:pt-20 -mb-8 md:-mb-12">
        <JustDropped />
      </div>

      {/* ================= TRENDING SCENEPACKS — numbered poster rail ================= */}
      <section className="relative pt-24 md:pt-32 pb-10 overflow-hidden">
        <div className="absolute -right-40 top-10 w-[560px] h-[560px] glow-garnet-sm pointer-events-none" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
          <SectionHead
            eyebrow={t("home.trending_scenepacks_eyebrow", "Most downloaded")}
            title={t("home.trending_scenepacks_title", "Trending Scenepacks")}
            link={{ to: "/scenepacks", label: "View all" }}
            className="mb-6"
          />
        </div>
        <TrendingRail packs={trendingPacks} loading={trendingLoading} onOpen={(pk) => setOpenPackId(pk.id)} />
      </section>

      {/* ================= TICKER ================= */}
      <div className="py-10 md:py-16 border-y border-white/5">
        <Marquee items={["Scenepacks", "4K REMUX", "Audio Edits", "1080p", "Built for editors", "Waveform previews"]} />
      </div>

      {/* ================= TRENDING AUDIOS ================= */}
      <AudiosSection
        audios={trendingAudios}
        eyebrow={t("home.trending_audios_eyebrow", "Trending audios")}
        title={t("home.trending_audios_title", "What editors are using")}
        subtitle={t("home.trending_audios_subtitle", "Hear it before you download it. Every audio has a waveform preview, and the player follows you around the site.")}
      />

      {/* ================= EDITS SHOWCASE ================= */}
      <EditsSection
        edits={edits}
        eyebrow={t("home.edits_eyebrow", "Made with ALPHA")}
        title={t("home.edits_title", "Edits in the wild")}
      />

      {/* ================= AA+ TEASER ================= */}
      <AaPlusTeaser
        title={t("home.aa_plus_title", "Unlock the vault.")}
        subtitle={t("home.aa_plus_subtitle", "Members-only scenepacks. Exclusive audio drops. Early access to new releases. Priority on pack requests. Everything an editor needs.")}
        perks={["Exclusive 8K scenepacks", "Early access drops", "Scenepack requests priority", "Discord roles"]}
      />

      <PackDrawer
        packId={openPackId}
        open={!!openPackId}
        onOpenChange={(v) => { if (!v) setOpenPackId(null); }}
      />
    </div>
    </MotionConfig>
  );
}

/* ---- Full-bleed tilted poster wall behind the hero, Netflix-style ---- */
// true on phones — they skip the poster wall (30 full-size images + a big rotated, animated layer)
function useIsPhone() {
  const [phone, setPhone] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setPhone(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return phone;
}

function PosterWall({ packs, loading }) {
  const phone = useIsPhone();
  if (phone) {
    return (
      <div className="absolute inset-0 bg-[#121212]" aria-hidden="true">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] glow-garnet-lg" />
        <div className="absolute bottom-0 -left-24 w-[360px] h-[360px] glow-garnet-md" />
      </div>
    );
  }
  return <PosterWallDesktop packs={packs} loading={loading} />;
}

function PosterWallDesktop({ packs, loading }) {
  // Several trending scenepacks can legitimately share one thumbnail (e.g.
  // multiple character packs cut from the same movie, using the movie poster —
  // see the GROUP BY movie_title path in backend/src/routes/scenepacks.ts).
  // The poster wall has no caption to tell those apart, so without deduping,
  // the same picture visibly repeats close together — dedupe by URL first and
  // only fall back to repeats once every distinct image has been used once.
  const urls = Array.from(new Set((packs || []).map((p) => p.thumbnail_url).filter(Boolean)));
  const [driftHeight, setDriftHeight] = useState(0);
  const roRef = useRef(null);
  const firstGridRef = useRef(null);
  const secondGridRef = useRef(null);

  const COLS = 6;
  const ROWS = 5;
  const tiles = Array.from({ length: COLS * ROWS }, (_, i) => (urls.length ? urls[i % urls.length] : null));

  // Measures the pitch from the top of the first grid copy to the top of the
  // second — NOT just the first grid's own offsetHeight. The two copies sit
  // in a flex column sharing the same gap-2 used *inside* each grid, so the
  // seam between them needs that same gap folded into the drift distance.
  // offsetHeight alone (the old approach) undercounts by exactly one gap,
  // which visibly pinches the seam every loop — the two copies looked
  // welded together right where every other row had breathing room.
  const measure = useCallback(() => {
    if (firstGridRef.current && secondGridRef.current) {
      setDriftHeight(secondGridRef.current.offsetTop - firstGridRef.current.offsetTop);
    }
  }, []);

  // A callback ref, not a useEffect: this fires the instant the real grid
  // element mounts (which only happens once packs have loaded), so the
  // measurement always happens against the actual node — no dependency
  // array to get wrong, no stale "it fired too early" bug.
  const measureRef = useCallback((node) => {
    firstGridRef.current = node;
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (node) {
      const ro = new ResizeObserver(measure);
      ro.observe(node);
      roRef.current = ro;
      // Both grid copies mount in the same commit, so secondGridRef is
      // already attached by the time this runs on the next frame.
      requestAnimationFrame(measure);
    }
  }, [measure]);

  if (loading || urls.length === 0) {
    return <div className="absolute inset-0 bg-[#121212]" />;
  }

  // The wrapper below is deliberately NOT centered on the combined height of
  // both grid copies (that would be `-translate-y-1/2`, i.e. -50% of a box
  // that's 2 copies tall). Centering on the full two-copy stack means the
  // drift — which travels exactly one copy's height — carries the visible
  // window from the stack's middle all the way to its bottom *edge*, so the
  // last stretch of every 90s cycle scrolls past the last row into empty
  // background before snapping back. Centering on one grid's own midpoint
  // (driftHeight / 2) instead keeps the visible window inside real content
  // at every point in the cycle — verified by sampling coverage across the
  // full 0–100% phase range with no gaps, vs. up to ~50% blank near the end
  // with the old centering.
  const rotWrapStyle = {
    transform: `translateX(-50%) translateY(${driftHeight ? -driftHeight / 2 : 0}px) rotate(-8deg)`,
  };

  return (
    <div className="absolute inset-0 overflow-hidden bg-[#121212]">
      <div className="absolute left-1/2 top-1/2 w-[150%]" style={rotWrapStyle}>
        <div
          className="poster-wall-drift flex flex-col gap-2"
          style={driftHeight ? { "--drift-distance": `${driftHeight}px` } : undefined}
        >
          <div ref={measureRef}>
            <PosterGrid tiles={tiles} cols={COLS} />
          </div>
          <div ref={secondGridRef}>
            <PosterGrid tiles={tiles} cols={COLS} />
          </div>
        </div>
      </div>

      {/* Legibility overlay — darkest under the text on the left, tapering out to the right */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#121212] via-[#121212]/88 to-[#121212]/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-[#121212]/60" />
    </div>
  );
}

function PosterGrid({ tiles, cols }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {tiles.map((url, i) => (
        <div key={i} className="aspect-[2/3] rounded-sm overflow-hidden opacity-80">
          {url && <SmartImg src={url} width={320} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
        </div>
      ))}
    </div>
  );
}

/* ---- Inline stat, part of a single flowing strip rather than boxed tiles ---- */
function StatInline({ value, label }) {
  return (
    <div className="pr-6">
      <div className="font-display font-black text-2xl leading-none">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mt-1.5 whitespace-nowrap">{label}</div>
    </div>
  );
}

/* ---- A small waveform tick — the same signature motif used as a divider ---- */
function WaveTick() {
  const heights = [6, 12, 8, 16, 8];
  return (
    <div className="flex items-end gap-[3px] h-4 mr-6 opacity-40 flex-shrink-0">
      {heights.map((h, i) => (
        <span key={i} className="w-[2px] bg-[#FF8090] rounded-full" style={{ height: `${h}px` }} />
      ))}
    </div>
  );
}

/* ---- Full-width waveform seam closing out the hero — a quiet static divider ---- */
function WaveformSeam() {
  const bars = useMemo(
    () => Array.from({ length: 64 }).map((_, i) => 6 + (Math.sin(i * 0.35) * 0.5 + 0.5) * 26),
    []
  );
  return (
    <div className="relative border-t border-white/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex items-end gap-[3px] h-10 overflow-hidden opacity-30">
          {bars.map((h, i) => (
            <span
              key={i}
              className="flex-1 bg-gradient-to-t from-[#880015] to-[#FF8090] rounded-full"
              style={{ height: `${h}px` }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-sm border border-white/10 flex items-center justify-center text-[#FF8090] flex-shrink-0">{icon}</div>
      <div>
        <div className="font-display font-bold text-sm">{title}</div>
        <div className="text-xs text-white/50 mt-1">{desc}</div>
      </div>
    </div>
  );
}
